import { $, must } from './dom.ts';
import type { AssignmentConfig, SiteMetadata, TestResults } from './evaluator-types.ts';
import { describe, fetchOk } from './fetch-helpers.ts';
import {
  MAX_RECONNECT_ATTEMPTS,
  reconnectDelayMs,
  type SessionEnd,
  shouldPark,
  shouldReconnect,
} from './reconnect.ts';
import {
  type DataFile,
  type Message,
  removeReplTab,
  type TerminalBackend,
  TerminalEngine,
} from './terminal-engine.ts';

/*
 * Interactive Java, backing the right-hand terminal pane of an interactive
 * coding assignment (layouts/java-terminal.njk). The same TerminalEngine
 * (xterm.js + readline) as interactive Python, but the program runs as a
 * subprocess on the Java runner (Server.java's /ws/run endpoint) rather than
 * in the browser:
 *
 *   - Run opens a WebSocket and sends the editor code; the server compiles it
 *     and spawns it.
 *   - The program's merged stdout/stderr streams back as binary frames.
 *   - Committed input lines go up as JSON "stdin" messages; the process's
 *     stdin pipe buffers type-ahead for free.
 *   - The socket's lifetime is the run's lifetime: the server finishes with a
 *     JSON "exit" message and closes; closing from our end kills the process.
 *
 * No SharedArrayBuffer or cross-origin isolation is needed — the blocking
 * read happens server-side in a real process.
 */

const encoder = new TextEncoder();

type Kind = 'program' | 'repl';

/*
 * The /ws/run protocol (Server.java, WsRunSession.java): one JSON "run"
 * message up to start things, then the program's output as binary frames
 * down, further JSON messages (stdin/resize/eof/stop) up, and JSON control
 * messages down until the closing "exit".
 */
type RunMessage = {
  type: 'run';
  mode?: string;
  heapMb?: number;
  dataFiles: { name: string; contentBase64: string }[];
  // PTY sessions (raw mode) size themselves and take no code...
  cols?: number;
  rows?: number;
  // ...compiled runs take the code (plus any extra editor tabs) instead.
  code?: string;
  extraSources?: string[];
};

// The JShell tab's load-editor-code-into-the-session message.
type LoadMessage = {
  type: 'load';
  code: string;
  extraSources?: string[];
};

export type ControlMessage =
  | { type: 'exit'; code: number; reason: string }
  | { type: 'compile-error'; message?: string }
  | { type: 'error'; message: string };

// Variations on the /ws/run protocol a backend can speak. The defaults are
// the interactive-Java assignment page's: kind 'program' is a plain compiled
// run and kind 'repl' is a jshell session on the shared runner. The shell and
// GUI pages reuse the same backend with a different mode (bash / gui) and, for
// per-user-machine sessions, the web app's authenticated /machine/ws/run
// proxy path instead of the runner's own /ws/run.
export type BackendOptions = {
  mode?: string; // run-message mode ("jshell", "bash", "gui"); omit to compile and run code
  wsPath?: string; // default '/ws/run'
  notice?: string; // dim line printed at run start while a JVM/machine spins up
};

// One WebSocket per run. kind 'program' compiles and runs the editor code
// over pipes, with the engine's readline doing echo and line editing. kind
// 'repl' starts a PTY-backed session (jshell by default — no code sent) in
// raw passthrough mode: the program's own line editor (JLine, bash readline)
// does the echo, editing, history, and completion, keystrokes go up verbatim
// as binary frames (^C becomes SIGINT via the PTY and interrupts the running
// snippet/job), and terminal resizes are forwarded so it knows its width.
export class JavaWsBackend implements TerminalBackend {
  kind: Kind;
  raw: boolean;
  engine!: TerminalEngine;
  config: AssignmentConfig;
  serverUrl: string | null;
  mode: string | undefined;
  wsPath: string;
  notice: string | null;

  socket: WebSocket | null = null;
  runEnded = true; // no exit/error expected on the current socket
  // How the last run ended (see reconnect.ts): the session loops use it to
  // tell "the student typed exit" from "the server went away".
  lastEnd: SessionEnd = 'exit';

  // The assignment's other editor tabs, set per run by the evaluator's
  // execute(). Compiled alongside the main code server-side (both the plain
  // and gui modes pass them to the compiler); meaningless for PTY sessions
  // (jshell/bash), which take no code.
  extraSources: string[] = [];

  constructor(
    kind: Kind,
    config: AssignmentConfig,
    serverUrl: string | null,
    opts: BackendOptions = {},
  ) {
    this.kind = kind;
    this.raw = kind === 'repl';
    this.config = config;
    this.serverUrl = serverUrl;
    this.mode = opts.mode ?? (kind === 'repl' ? 'jshell' : undefined);
    this.wsPath = opts.wsPath ?? '/ws/run';
    this.notice = opts.notice ?? (kind === 'repl' ? 'Starting jshell…' : null);
  }

  attach(engine: TerminalEngine) {
    this.engine = engine;
    // '' is a valid serverUrl meaning same-origin (the dev-mode runner proxy);
    // only null/undefined means no runner is configured at all.
    if (this.serverUrl == null) {
      engine.disable(
        'No run server is configured (SERVER_URL), so interactive',
        'Java is unavailable.',
      );
      return;
    }
    engine.state = 'idle';
    if (this.kind === 'program') {
      engine.term.writeln('\x1b[90mReady — click ▶ Run to run your program.\x1b[0m');
    }
  }

  whenReady(): Promise<void> {
    return Promise.resolve();
  }

  start(code: string, dataFiles: DataFile[]) {
    // attach() disabled the engine when serverUrl was null, so a run can only
    // start with one configured.
    const serverUrl = must(this.serverUrl, 'run server URL');
    const base =
      serverUrl === ''
        ? `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`
        : serverUrl.replace(/^http/, 'ws');
    const socket = new WebSocket(`${base}${this.wsPath}`);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    this.runEnded = false;
    this.lastEnd = 'exit';
    let opened = false;

    // A jshell JVM (or a whole per-user machine) takes a few seconds to come
    // up; say so rather than sitting on a blank terminal.
    if (this.notice) {
      this.engine.term.writeln(`\x1b[90m${this.notice}\x1b[0m`);
    }

    socket.onopen = () => {
      opened = true;
      const msg: RunMessage = {
        type: 'run',
        heapMb: this.config.heapMb,
        dataFiles: dataFiles.map((f) => ({ name: f.name, contentBase64: toBase64(f.data) })),
      };
      if (this.mode) msg.mode = this.mode;
      if (this.raw) {
        // PTY sessions size themselves to the terminal and take no code.
        msg.cols = this.engine.term.cols;
        msg.rows = this.engine.term.rows;
      } else {
        msg.code = code;
        if (this.extraSources.length) msg.extraSources = this.extraSources;
      }
      socket.send(JSON.stringify(msg));
    };

    socket.onmessage = (e: MessageEvent) => {
      if (socket !== this.socket) return; // a stale socket from a torn-down run
      if (e.data instanceof ArrayBuffer) {
        this.engine.term.write(new Uint8Array(e.data));
      } else {
        this.onControl(JSON.parse(e.data));
      }
    };

    socket.onclose = () => {
      if (socket !== this.socket) return;
      if (!this.runEnded) {
        // The server (or the network) dropped us without an exit message —
        // a deploy, a wifi blip, the runner restarting. A session loop
        // (jshell, shell) reconnects on its own; a program run can't be
        // resumed, so offer the way back instead of a dead pane.
        this.lastEnd = opened ? 'dropped' : 'unreachable';
        if (this.kind === 'program') {
          this.engine.term.writeln(
            opened
              ? '\x1b[31m— connection lost — the server may be restarting; click ▶ Run to try again —\x1b[0m'
              : '\x1b[31m— couldn’t reach the run server (it may be restarting) — click ▶ Run to try again —\x1b[0m',
          );
        } else {
          this.engine.term.writeln(
            opened ? '\x1b[31m— connection lost —\x1b[0m' : '\x1b[31m— couldn’t connect —\x1b[0m',
          );
        }
        this.endRun();
      }
    };

    socket.onerror = () => {
      // onclose fires afterwards and reports; nothing useful in the event.
      console.log('Interactive Java socket error');
    };
  }

  onControl(msg: ControlMessage) {
    switch (msg.type) {
      case 'exit':
        // A REPL exiting normally (Ctrl-D, /exit) just restarts (see
        // startReplLoop), so don't also print a finish line for it.
        if (this.kind === 'program' || msg.reason !== 'exited') {
          this.engine.term.writeln(`\x1b[90m${exitDescription(msg.code, msg.reason)}\x1b[0m`);
        }
        this.endRun();
        break;
      case 'compile-error':
        this.engine.term.writeln("\x1b[31mYour code didn't compile:\x1b[0m");
        for (const line of String(msg.message ?? '').split('\n')) {
          this.engine.term.writeln(`\x1b[31m${line}\x1b[0m`);
        }
        this.endRun();
        break;
      case 'error':
        this.engine.term.writeln(`\x1b[31m${msg.message}\x1b[0m`);
        this.endRun();
        break;
    }
  }

  // The current run is over (exit/compile-error/error received, or the socket
  // dropped). Make sure the socket is closed and resolve the run.
  endRun() {
    this.runEnded = true;
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.engine.finishRun();
  }

  send(msg: object) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(msg));
    }
  }

  sendLine(line: string) {
    this.send({ type: 'stdin', line });
  }

  // Load editor code into a live jshell session and run its main — the JShell
  // tab's meaning of the run button. The server prepares the code (package
  // strip, "Loaded from the editor" summary) and types an /open at the
  // prompt, then the call that runs the entry point when the code has one the
  // Java launcher would run (WsRunSession.load / JShellSource.java); jshell
  // replaces same-named definitions, so each click syncs the session to the
  // editor's latest and reruns the program.
  load(code: string, extraSources: string[]) {
    if (this.socket?.readyState === WebSocket.OPEN && !this.runEnded) {
      const msg: LoadMessage = { type: 'load', code };
      if (extraSources.length) msg.extraSources = extraSources;
      this.socket.send(JSON.stringify(msg));
    } else {
      this.engine.term.writeln(
        '\x1b[90mjshell isn’t running yet — click ▶ again in a moment.\x1b[0m',
      );
    }
  }

  // Raw keystrokes for the PTY-backed jshell session, as binary frames. The
  // engine only calls this in raw mode, so ^C, arrows, and tab all reach
  // jshell's JLine directly.
  sendRaw(data: string) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(encoder.encode(data));
    }
  }

  onTermResize(cols: number, rows: number) {
    if (this.raw) this.send({ type: 'resize', cols, rows });
  }

  sendEOF() {
    this.send({ type: 'eof' });
  }

  // Program runs only (the jshell tab is raw mode, so its ^C bytes go
  // straight through sendRaw and become SIGINT via the PTY): Ctrl-C and Stop
  // both kill the process; the server answers with an exit message (reason
  // "stopped") which is what prints the "— stopped —" line.
  onCtrlC() {
    this.engine.term.write('\r\n');
    this.stop();
  }

  stop() {
    this.send({ type: 'stop' });
  }

  // Tear down without waiting for the server (rerun-while-running): closing
  // the socket kills the process server-side.
  hardStop() {
    this.runEnded = true;
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.engine.finishRun();
  }
}

/*
 * Keep a PTY session (jshell, bash) running in `engine`: a clean exit (the
 * student typed exit / Ctrl-D, or Restart) just starts a fresh one, and a
 * session the server or network took away is reconnected with backoff
 * (reconnect.ts) — "reconnecting in 4s…" rather than a dead pane, since
 * the usual cause is a deploy and the session's machine may well have
 * survived. A server that stays down gets MAX_RECONNECT_ATTEMPTS tries,
 * then the loop parks with a hint; the browser coming back online, or the
 * page's Restart control (`restart()`), un-parks it. Sessions that die
 * within a few seconds of connecting count as failures too, so a flapping
 * server backs off instead of being hammered.
 *
 * An ending nobody is there for is different: when the user hasn't typed
 * for IDLE_PARK_MS (reconnect.ts says why), the loop parks on the next
 * keystroke instead of starting another session — a forgotten JShell tab
 * must not hold a runner machine up all night. Restart un-parks that too;
 * coming back online does not (nobody asked).
 */
export const keepSessionAlive = (
  engine: TerminalEngine,
  backend: JavaWsBackend,
  name: string,
): { restart: () => void } => {
  let failures = 0;
  let parked = false; // gave up reconnecting; only Restart or `online` resumes
  let idle = false; // waiting for a keystroke, not for the network
  let manual = false; // the current run was ended by restart(), not the server
  let wake: (() => void) | null = null;

  const dim = (text: string) => engine.term.writeln(`\x1b[90m${text}\x1b[0m`);
  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      const t = setTimeout(resolve, ms);
      wake = () => {
        clearTimeout(t);
        resolve();
      };
    }).finally(() => {
      wake = null;
    });
  const untilTyped = () =>
    new Promise<void>((resolve) => {
      const next = engine.nextInput();
      idle = true;
      void next.promise.then(resolve);
      wake = () => {
        next.cancel();
        resolve();
      };
    }).finally(() => {
      idle = false;
      wake = null;
    });

  const loop = async () => {
    parked = false;
    while (engine.usable) {
      const startedAt = Date.now();
      await engine.run('', []);
      if (!engine.usable) return;
      const quick = Date.now() - startedAt < 3000;
      const wasManual = manual;
      manual = false;
      if (!wasManual && shouldPark(Date.now() - engine.lastInputAt)) {
        failures = 0;
        dim(`[${name} closed while idle — press any key to reconnect]`);
        await untilTyped();
      } else if (!wasManual && (shouldReconnect(backend.lastEnd) || quick)) {
        failures += 1;
        if (failures > MAX_RECONNECT_ATTEMPTS) {
          engine.term.writeln(
            `\x1b[31m— couldn’t reconnect to ${name}; click Restart (or reload the page) to try again —\x1b[0m`,
          );
          parked = true;
          return;
        }
        const delay = reconnectDelayMs(failures);
        dim(`[reconnecting to ${name} in ${Math.round(delay / 1000)}s…]`);
        await sleep(delay);
      } else {
        failures = 0;
        dim(`[restarting ${name}]`);
      }
    }
  };

  window.addEventListener('online', () => {
    if (parked) void loop();
    else if (!idle) wake?.();
  });

  void loop();
  return {
    // Restart: a running session is torn down (the loop starts a fresh
    // one); a loop waiting out a backoff or parked after giving up goes
    // again right now.
    restart() {
      failures = 0;
      if (parked) {
        void loop();
      } else if (wake) {
        wake();
      } else {
        manual = true;
        engine.hardStop();
      }
    },
  };
};

const exitDescription = (code: number, reason: string): string => {
  switch (reason) {
    case 'stopped':
      return '— stopped —';
    case 'timeout':
      return '— stopped: the program ran too long —';
    case 'output-limit':
      return '— stopped: too much output —';
    default:
      return code === 0 ? '— program finished —' : `— program finished (exit code ${code}) —`;
  }
};

// Base64-encode an ArrayBuffer in chunks (String.fromCharCode has an argument
// limit well below a big file's size).
const toBase64 = (data: ArrayBuffer): string => {
  const bytes = new Uint8Array(data);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
};

/*
 * The evaluator index.ts instantiates: owns the program terminal and
 * implements the execute() contract (run the editor code; resolve with no
 * test results when the program ends). Like InteractivePython, the right pane
 * is tabbed: "Terminal" runs the editor program and "JShell" is a lazily
 * created jshell session (created on first open, restarted whenever it
 * exits). The run button follows the visible tab — on Terminal it compiles
 * and runs the editor code as a program; on JShell it loads the editor's
 * definitions into the session and runs its main, if it has one
 * (JavaWsBackend.load). A page can make JShell
 * the initial tab by marking its tab/view active in the markup (see the
 * jshell playground).
 */
class InteractiveJava {
  config: AssignmentConfig;
  message: Message;
  serverUrl: string | null;
  programBackend: JavaWsBackend;
  program: TerminalEngine;
  repl: TerminalEngine | null = null;
  replBackend: JavaWsBackend | null = null;
  replSession: { restart: () => void } | null = null;
  activeTab = 'terminal';

  stopButton: HTMLButtonElement | null;
  restartButton: HTMLButtonElement | null;

  constructor(
    config: AssignmentConfig,
    message: Message,
    _username: string,
    metadata: SiteMetadata,
  ) {
    this.config = config;
    this.message = message;
    this.serverUrl = metadata?.serverUrl ?? null;
    if (config.replTab === false) removeReplTab();
    this.programBackend = new JavaWsBackend('program', config, this.serverUrl);
    this.program = new TerminalEngine(
      '#terminal',
      this.programBackend,
      message,
      null, // the exit control message prints the finish line
    );

    this.stopButton = $('.terminal-tabs .stop-code') as HTMLButtonElement | null;
    if (this.stopButton) this.stopButton.onclick = () => this.program.stop();
    this.restartButton = $('.terminal-tabs .restart-repl') as HTMLButtonElement | null;
    if (this.restartButton) this.restartButton.onclick = () => this.replSession?.restart();

    for (const tab of document.querySelectorAll<HTMLElement>('.terminal-tabs .tab[data-tab]')) {
      tab.onclick = () => this.showTab(must(tab.dataset.tab, 'data-tab on terminal tab'));
    }

    // The jshell playground marks the repl tab active in the markup; honor
    // that by starting jshell right away.
    const active = document.querySelector('.terminal-tabs .tab.active') as HTMLElement | null;
    if (active?.dataset.tab === 'repl') this.showTab('repl');
  }

  showTab(name: string) {
    this.activeTab = name;
    for (const v of document.querySelectorAll('.terminal-view')) {
      v.classList.toggle('active', (v as HTMLElement).dataset.view === name);
    }
    for (const t of document.querySelectorAll('.terminal-tabs .tab')) {
      t.classList.toggle('active', (t as HTMLElement).dataset.tab === name);
    }
    if (this.stopButton) this.stopButton.hidden = name !== 'terminal';
    if (this.restartButton) this.restartButton.hidden = name !== 'repl';

    if (name === 'repl' && !this.repl) {
      // Create + start jshell only when first opened (its container is now
      // visible, so the terminal sizes correctly, and the server doesn't pay
      // for a jshell JVM unless it's actually used).
      this.replBackend = new JavaWsBackend('repl', this.config, this.serverUrl);
      this.repl = new TerminalEngine('#repl-terminal', this.replBackend, this.message, null);
      // Keep a jshell prompt running on the JShell tab: when it exits
      // (Ctrl-D, /exit, or a Restart) start a fresh one; when the server
      // drops it, reconnect.
      this.replSession = keepSessionAlive(this.repl, this.replBackend, 'jshell');
    }

    // Only the terminal views hold a terminal to refit and focus — the
    // combined page's Tests tab (interactive-java-tested.ts) holds the
    // pills, and xterm can't measure the now-hidden terminal anyway.
    const active = name === 'repl' ? this.repl : name === 'terminal' ? this.program : null;
    requestAnimationFrame(() => {
      active?.refit();
      active?.focus();
    });
  }

  // A missing data file is a visible "Couldn't load <file> (404)" (the
  // run rejects; index.ts shows it), never a 404 page written into the
  // program's working directory as data.
  async #fetchDataFiles(): Promise<DataFile[]> {
    const files: string[] = this.config.dataFiles ?? [];
    return Promise.all(
      files.map(async (f) => {
        const res = await fetchOk(new URL(f, window.location.href)).catch((e) => {
          throw new Error(describe(e, `load ${f}`));
        });
        return {
          name: must(f.split('/').pop(), `file name in ${f}`),
          data: await res.arrayBuffer(),
        };
      }),
    );
  }

  async execute(
    code: string,
    _filename: string,
    others: { file: string; content: string }[] = [],
  ): Promise<TestResults> {
    // The run button acts on the visible tab: on JShell it loads the editor
    // code into the session and runs its main there (JavaWsBackend.load)
    // instead of running it as a separate program.
    if (this.activeTab === 'repl' && this.replBackend) {
      this.replBackend.load(
        code,
        others.map((o) => o.content),
      );
      this.repl?.focus();
      return Object.create(null);
    }
    const dataFiles = await this.#fetchDataFiles();
    this.programBackend.extraSources = others.map((o) => o.content);
    await this.program.run(code, dataFiles);
    return Object.create(null);
  }
}

const evaluator = (
  config: AssignmentConfig,
  message: Message,
  username: string,
  metadata: SiteMetadata,
) => new InteractiveJava(config, message, username, metadata);

export default evaluator;
